import React, { useContext } from 'react';
import { Query } from 'react-apollo';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { defineMessages } from '@sm/intl';
import { Typography, Card } from '@sm/wds-react';
import { StaticContext, ErrorCard, Helmet } from '@sm/webassets';
import { sanitizeString } from '@sm/utils';

import NavBar from '../../components/NavBar';
import SearchBox from '../../components/SearchBox';
import AppListingList from '../../components/AppListingList';
import AppListingSkeleton from '../../components/AppListingList/AppListingSkeleton';
import AppDirectoryBasePage from '../AppDirectoryBasePage';

import publishedApplicationListingsByKeywordQuery from './search.graphql';

const FETCH_LIMIT = 50;

export const COPY = defineMessages({
  SEARCH_RESULTS: {
    id: 'Search.Results',
    defaultMessage: 'Search results for',
    description: '[Type: Header][Vis.: med] - A small phrase describing the keyword used to search for apps',
  },
  RESULTS_NOT_FOUND: {
    id: 'Search.ResultsNotFound',
    defaultMessage: 'No apps were found for this keyword',
    description:
      '[Type: Paragraph][Vis.: low] - A message informing that no apps were found for the keyword used to search for apps.',
  },
  FILL_IN_KEYWORD: {
    id: 'Search.FillInKeyword',
    defaultMessage: 'You need to fill in a keyword to search for apps.',
    description:
      '[Type: Paragraph][Vis.: low] - A message informing that a keyword needs to be informed in order to search for apps',
  },
});

const Search = ({ location: { search } }) => {
  const {
    environment: { languageId, subdomain, tld },
  } = useContext(StaticContext);
  const params = new URLSearchParams(search);
  const keyword = sanitizeString(params.get('q'));

  return (
    <AppDirectoryBasePage id="App Directory - Search" navbar={<NavBar />} pageMetricsAttributes={{ keyword }}>
      <Helmet>
        <title>SurveyMonkey Apps &amp; Integrations Search Results</title>
      </Helmet>
      <Query
        query={publishedApplicationListingsByKeywordQuery}
        skip={!keyword}
        variables={{
          subdomain,
          tld,
          language: languageId,
          keyword,
          page: 1,
          pageSize: FETCH_LIMIT,
        }}
      >
        {({ loading, error, data }) => {
          return (
            <>
              <SearchBox location="app directory search" />
              <Typography component="section" variant="sectionTitle" className="wds-m-t-4 wds-m-b-4">
                {`${COPY.SEARCH_RESULTS.defaultMessage} "${keyword}"`}
              </Typography>
              <Choose>
                <When condition={error}>
                  <ErrorCard />
                </When>
                <When condition={loading}>
                  <AppListingSkeleton data-testid="Search__LoadingSkeleton" />
                </When>
                <When condition={data && data.publishedApplicationListingsByKeyword.items.length === 0}>
                  <Card className="wds-type--center sm-search--no-apps">
                    <Typography>{COPY.RESULTS_NOT_FOUND.defaultMessage}</Typography>
                  </Card>
                </When>
                <When condition={keyword && data}>
                  <AppListingList apps={data.publishedApplicationListingsByKeyword.items} />
                </When>
                <Otherwise>
                  <Card className="wds-type--center sm-search--no-keyword">
                    <Typography>{COPY.FILL_IN_KEYWORD.defaultMessage}</Typography>
                  </Card>
                </Otherwise>
              </Choose>
            </>
          );
        }}
      </Query>
    </AppDirectoryBasePage>
  );
};

Search.propTypes = {
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
};

export default withRouter(Search);
